import { useState } from "react";
import { User, Route, MessageSquare, Bookmark, Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action?: "save" | "route" | "post" | "default";
  initialSignUp?: boolean;
}

const actions = {
  default: {
    title: "Maak een gratis account",
    description: "Met een account kun je routes maken, opslaan en je vondsten delen."
  },
  save: {
    title: "Toevoegen aan bucketlist",
    description: "Maak een gratis account om winkels en routes aan je bucketlist toe te voegen."
  },
  route: {
    title: "Route maken",
    description: "Maak een gratis account om je eigen kringlooproutes te maken en te delen."
  },
  post: {
    title: "Deel je buit",
    description: "Maak een gratis account om je mooiste vondsten te delen met de community."
  }
};

const features = [
  { icon: Bookmark, text: "Routes & winkels opslaan" },
  { icon: MessageSquare, text: "1 post per week" }
];

export function LoginModal({ open, onOpenChange, action = "default", initialSignUp = false }: LoginModalProps) {
  const { t } = useI18n();
  const [isSignUp, setIsSignUp] = useState(initialSignUp);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [resetMode, setResetMode] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const { refreshAuth } = useAuth();
  const content = actions[action];

  // Email/password login
  const handleEmailLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        // Sign up
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/confirmed`,
            data: {
              // Store username in metadata for easier access
              username: username,
              consent_marketing: marketingConsent,
              consent_analytical: true, // Technical/Basic tracking
              consent_ads: false        // Not explicitly asked yet
            }
          }
        });

        if (error) {
          setError(error.message || t("loginModal.signupFailed"));
          throw error;
        }

        // Check if user was created
        if (!data.user) {
          const errorMsg = t("loginModal.accountCreateFailed");
          setError(errorMsg);
          throw new Error(errorMsg);
        }

        // If email confirmation is disabled, user is immediately signed in
        if (data.session) {
          // Refresh auth context (this handles /api/users/me sync centrally once).
          await refreshAuth?.();
          onOpenChange(false);
          return;
        }

        // Email confirmation is required
        setError(null);
        alert(t("loginModal.accountCreatedCheckEmail"));
        setIsSignUp(false); // Switch to login mode
      } else {
        // Sign in with persistent session (28 days rolling expiration)
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: {
            // Session will be persisted in localStorage
            // Auto-refresh is enabled, extending session on each use
          }
        });

        if (error) {
          setError(error.message || t("loginModal.loginFailed"));
          throw error;
        }

        // Session is automatically saved to localStorage (28 days)
        // Auto-refresh will extend it each time user uses the app
        if (data.session) {
          // Refresh auth context (centralized sync).
          await refreshAuth?.();
          onOpenChange(false);
        } else {
          const errorMsg = t("loginModal.couldNotLogin");
          setError(errorMsg);
          throw new Error(errorMsg);
        }
      }
    } catch (err: any) {
      // Error is already set in the catch blocks above
      // But ensure we have a message if it wasn't set
      if (!error) {
        setError(err.message || t("loginModal.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!email) {
      setError("Voer je e-mailadres in.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      alert(t("loginModal.resetPasswordSent"));
      setResetMode(false);
    } catch (err: any) {
      setError(err.message || t("loginModal.genericError"));
    } finally {
      setLoading(false);
    }
  };


  // Google OAuth login
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/account`,
          queryParams: {
            access_type: "offline",
            prompt: "select_account", // Better UX: shows account picker
          },
          // Custom scopes - alleen wat nodig is voor basis login
          scopes: "email profile",
        }
      });

      if (error) {
        setError(error.message || t("loginModal.googleLoginFailed"));
        throw error;
      }
    } catch (err: any) {
      setError(err.message || t("loginModal.googleLoginFailed"));
      setLoading(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">
            {resetMode ? t("loginModal.resetPassword") : isSignUp ? t("loginModal.createAccount") : t("nav.login")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {resetMode
              ? "Voer je e-mailadres in om je wachtwoord te herstellen."
              : isSignUp
                ? t("loginModal.createToUseFeatures")
                : content.description}
          </DialogDescription>
        </DialogHeader>

        {/* Email/Password form */}
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            {isSignUp && (
              <Input
                type="text"
                placeholder={t("loginModal.username") || "Gebruikersnaam"}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                autoComplete="username"
                required
              />
            )}
            <Input
              type="email"
              placeholder={t("loginModal.email") || "E-mailadres"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="email"
            />
            {!resetMode && (
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("loginModal.password") || "Wachtwoord"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && email && password) {
                      handleEmailLogin();
                    }
                  }}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                  disabled={loading}
                  aria-label={showPassword ? "Verberg wachtwoord" : "Toon wachtwoord"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-destructive text-center">{error}</div>
          )}

          <Button
            size="lg"
            className="w-full"
            onClick={resetMode ? handleResetPassword : handleEmailLogin}
            disabled={loading || !email || (!resetMode && !password) || (isSignUp && (!username || !agreedToTerms))}
            data-testid="button-login"
          >
            {loading ? "..." : resetMode ? t("loginModal.send") : isSignUp ? t("loginModal.createAccount") : t("nav.login")}
          </Button>

          {!isSignUp && (
            <div className="text-center">
              <button
                onClick={() => {
                  setResetMode(!resetMode);
                  setError(null);
                }}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
                disabled={loading}
              >
                {resetMode ? "Terug naar inloggen" : t("loginModal.forgotPassword")}
              </button>
            </div>
          )}

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t("loginModal.orContinueWith")}
              </span>
            </div>
          </div>

          {/* OAuth button */}
          <Button
            variant="outline"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {loading ? t("routeBuilder.saving") : t("loginModal.loginWithGoogle")}
          </Button>

          {/* Toggle sign up / sign in */}
          <div className="text-center text-sm">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError(null);
              }}
              className="text-primary hover:underline"
              disabled={loading}
            >
              {isSignUp
                ? t("loginModal.haveAccountLogin")
                : t("loginModal.noAccountCreate")}
            </button>
          </div>
        </div>

        {isSignUp && (
          <div className="space-y-3 mt-4 pt-4 border-t">
            <p className="text-sm font-medium text-center text-muted-foreground">
              {t("loginModal.freeAccountCan")}
            </p>
            {features.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        )}

        {isSignUp && (
          <div className="space-y-4 mt-6">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                className="mt-1"
              />
              <Label htmlFor="terms" className="text-xs leading-tight cursor-pointer">
                {t("loginModal.agreeTo")}{" "}
                <Link href="/voorwaarden" className="text-primary hover:underline">{t("footer.terms")}</Link>{" "}
                {t("loginModal.andThe")}{" "}
                <Link href="/privacy" className="text-primary hover:underline">{t("footer.privacy")}</Link>.
              </Label>
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="marketing"
                checked={marketingConsent}
                onCheckedChange={(checked) => setMarketingConsent(checked as boolean)}
                className="mt-1"
              />
              <Label htmlFor="marketing" className="text-xs leading-tight cursor-pointer">
                {t("loginModal.marketingOptIn")}
              </Label>
            </div>
          </div>
        )}

        <p className="text-[10px] text-center text-muted-foreground mt-4 italic">
          {t("loginModal.termsNotice")}
        </p>
      </DialogContent>
    </Dialog>
  );
}
